// src/components/blog/layouts/LatestPosts.tsx

'use client'

import Link from 'next/link'
import { motion } from 'motion/react'
import { cn } from '~/lib/utils'
import type { BlogPost } from '~/lib/blog/types'
import Image from 'next/image'
import { IconComponent } from '~/components/shared/IconComponent'
import { formatDate, formatDateTime } from '~/lib/utils/date'
import { getBlogListingImageUrl } from '~/lib/blog/utils/listingImage'

interface LatestPostsLayoutProps {
  posts: BlogPost[]
  className?: string
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
}

export function LatestPostsLayout({ posts, className }: LatestPostsLayoutProps): JSX.Element {
  return (
    <motion.div 
      variants={container}
      initial="hidden"
      animate="show"
      className={cn("grid gap-8", className)}
    >
      {posts.map((post) => (
        <motion.article
          key={post.id}
          variants={item}
          className="group relative grid grid-cols-1 sm:grid-cols-2 gap-8 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-all duration-200 hover:shadow-lg dark:border-gray-700 dark:bg-gray-800"
        >
          <div className="relative w-full h-full flex items-center">
            <div className="relative w-full pb-[100%]">
              <Link
                href={post.href}
                className="absolute inset-0 overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-700"
              >
                <Image
                  src={getBlogListingImageUrl(post)}
                  alt={post.title}
                  fill
                  sizes="(min-width: 1536px) 576px, (min-width: 1280px) 512px, (min-width: 1024px) 384px, (min-width: 640px) 50vw, 100vw"
                  className="object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </Link>
            </div>
          </div>
          <div className="flex flex-col justify-between h-full">
            <div className="space-y-4">
              {/* Topic & Date */}
              <div className="flex items-center justify-between">
                {post.topic && (
                  <Link
                    href={post.topic.href}
                    className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    {post.topic.title}
                  </Link>
                )}
                <time 
                  dateTime={formatDateTime(post.datetime)}
                  className="text-sm text-gray-500 dark:text-gray-400"
                >
                  {formatDate(post.date, 'full')}
                </time>
              </div>

              {/* Title & Description */}
              <div className="space-y-3">
                <Link href={post.href} className="group/link block">
                  <h3 className="text-xl sm:text-xl md:text-2xl lg:text-2xl font-bold text-gray-900 transition-colors group-hover/link:text-blue-600 dark:text-white dark:group-hover/link:text-blue-400 md:line-clamp-4">
                    {post.title}
                  </h3>
                  <p className="mt-2 text-base text-gray-600 sm:line-clamp-3 md:line-clamp-5 lg:line-clamp-6 dark:text-gray-300">
                    {post.description}
                  </p>
                </Link>
              </div>
            </div>

            {/* Author & Reading Time */}
            <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4 gap-2 dark:border-gray-700">
              <Link 
                href={post.author.href}
                className="flex items-center gap-3 group/author"
              >
                <div className="relative h-10 w-10 overflow-hidden rounded-full ring-2 ring-white/10">
                  <Image
                    src={post.author.imageUrl}
                    alt={post.author.name}
                    fill
                    sizes="40px"
                    style={{ objectFit: 'cover' }}
                    className="rounded-full"
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 transition-colors group-hover/author:text-blue-600 dark:text-white dark:group-hover/author:text-blue-400">
                    {post.author.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {post.author.role}
                  </p>
                </div>
              </Link>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <IconComponent name="RiTimeLine" className="h-4 w-4" />
                <span>{post.readingTime} min read</span>
              </div>
            </div>
          </div>
        </motion.article>
      ))}
    </motion.div>
  )
} 